import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, SearchIcon, BadgeDollarSignIcon, TargetIcon, EyeIcon, ArrowRightIcon } from 'lucide-react';
const differentiators = [{
  icon: UsersIcon,
  title: 'Experts',
  subtitle: 'IN TELECOM COMMUNICATIONS',
  description: 'We represent and connect you to the most comprehensive tech companies in Business Communications.',
  bg: 'bg-slate-100'
}, {
  icon: SearchIcon,
  title: 'Exclusive',
  subtitle: 'RESEARCH FOR YOUR BUSINESS',
  description: "We test each provider's flexibility to work with your business needs. We guarantee that we can match you with the perfect source for your communications services.",
  bg: 'bg-blue-200/60'
}, {
  icon: BadgeDollarSignIcon,
  title: 'Cost Effective',
  subtitle: 'TO SAVE ON YOUR BUDGET',
  description: 'We respect your budget as we design the most economic options for your business needs. We will recommend and adjust services in order to meet your budget.',
  bg: 'bg-blue-300/50'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function AboutPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-365">
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-366">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-367"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-368"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px] bg-primary/15 rounded-full blur-[100px] pointer-events-none" data-id="element-369"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-370">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-371">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-372">
              About Us
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-373">
              Your trusted partner in telecom expense reduction and optimization
              since day one.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 lg:py-28 bg-white" data-id="element-374">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-375">
          <div className="max-w-4xl mx-auto" data-id="element-376">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mb-16" data-id="element-377">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-378">
                Who We Are
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed" data-id="element-379">
                <p data-id="element-380">
                  We are a full-service telecommunications and cloud services
                  procurement company that helps businesses save money on
                  Internet access, phone services, VoIP, wireless, POS and
                  utility services by partnering with various providers to
                  create custom package offers that are totally tailored to
                  their needs.
                </p>
                <p data-id="element-381">
                  We are a market leader in Voice, Data, Hosted VoIP, UCaaS
                  (Unified Communication), Managed and Cloud Services, Data
                  Center, and a full range of multichannel services providing
                  high-quality and cost-effective network administration and
                  support solutions backed by almost twenty years of experience.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-382">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-383">
                Our Mission
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed" data-id="element-384">
                <p data-id="element-385">
                  Ambient Telecom was founded with the goal of providing
                  organizations with completely transparent billing and relevant
                  services and solutions to assist them in finding the right
                  service providers in the telecommunications and cloud service
                  industries.
                </p>
                <p data-id="element-386">
                  We are driven to assist our clients in assessing and
                  negotiating time-consuming complex telecom and IT service
                  provider selection decisions, transforming into a
                  one-stop-shop for everything in the cloud infrastructure and
                  telecommunications arena.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Mission & Vision Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16 max-w-4xl mx-auto" data-id="element-387">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-id="element-388">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5" data-id="element-389">
                <TargetIcon className="w-6 h-6 text-primary" data-id="element-390" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-391">
                Our Mission
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-392">
                To eliminate telecom overspending for every business we serve by
                providing transparent billing analysis, expert negotiation, and
                ongoing cost optimization.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-id="element-393">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5" data-id="element-394">
                <EyeIcon className="w-6 h-6 text-primary" data-id="element-395" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-396">
                Our Vision
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-397">
                To be the most trusted telecom expense reduction partner in the
                country — where every business has access to fair pricing and
                world-class connectivity.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-398">
        <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-399" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-400">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 text-center mb-16" data-id="element-401">
            What Makes Us Different?
          </h2>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-402">
            {differentiators.map((card, i) => {
            const Icon = card.icon;
            return <motion.div key={i} variants={cardVariants} className={`${card.bg} rounded-2xl p-8 md:p-10 flex flex-col min-h-[320px]`} data-id="element-403">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mb-6" data-id="element-404">
                    <Icon className="w-5 h-5 text-slate-300" data-id="element-405" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-406">
                    {card.title}
                  </h3>
                  <p className="text-sm font-bold text-slate-800 uppercase tracking-wide mb-4" data-id="element-407">
                    {card.subtitle}
                  </p>
                  <p className="text-slate-600 leading-relaxed" data-id="element-408">
                    {card.description}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-slate-50" data-id="element-409">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-410">
          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-411">
            {[{
            value: '20+',
            label: 'Years of Experience'
          }, {
            value: '500+',
            label: 'Businesses Served'
          }, {
            value: '$2M+',
            label: 'Client Savings Found'
          }, {
            value: '30%',
            label: 'Average Cost Reduction'
          }].map((stat, i) => <div key={i} data-id="element-412">
                <div className="text-3xl md:text-4xl font-extrabold text-primary mb-2" data-id="element-413">
                  {stat.value}
                </div>
                <div className="text-slate-600 font-medium" data-id="element-414">{stat.label}</div>
              </div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-415">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-416">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-417"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-418"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-419">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-420">
            Ready to see how much you can save?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-421">
            Let our team of telecom experts audit your current bills and show
            you exactly where you're overspending.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-422">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-423">
              Get Your Free Analysis
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-424" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-425">
              Call Us: +410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}